import type { ReactText } from 'react';
import React, { useEffect, useRef, useMemo } from 'react';
import { View } from '../ui';
import type { SpotEntry } from '@seaber/common/core/entries';
import { useSpotContractLazyQuery } from '../../graphql';
import type { SpotFormSaveType } from '../voyageCharter/SpotForm';
import SpotForm from '../voyageCharter/SpotForm';
import {
  isPersistedSpotOrder,
  useCreateSpotWithRelationsMutation,
  useUpdateSpotWithRelationsMutation,
} from '../voyageCharter/spot';
import { toast } from 'react-toastify';
import { assertDefined, isDefined } from '../../lib/util';
import omit from 'lodash/omit';
import { LoadingOverlay } from '../LoadingSpinner';
import { stringToNumOpt } from '../core/util';

interface SpotContractProps {
  spotAssignments: readonly SpotEntry[];
  onClose?: () => void;
  onVesselChange?: (
    voyageCharterId: number,
    shipownerOrgId: number | null,
    vesselId: number | null,
  ) => void;
  spotContractId: number | null;
}

const SpotContract = ({
  onVesselChange,
  spotAssignments,
  spotContractId,
}: SpotContractProps): JSX.Element | null => {
  const [doSpotModalLazyQuery, { data: spotData, loading: spotDataLoading }] =
    useSpotContractLazyQuery({
      fetchPolicy: 'network-only',
    });

  const existingSpot = useMemo(
    () => spotData?.voyageCharterById ?? undefined,
    [spotData],
  );

  const unsavedOrderIds = useMemo(() => {
    const savedOrderIds =
      existingSpot?.orderRelations.nodes
        .map((orderRel) => orderRel?.orderId)
        .filter(isDefined) ?? [];
    const assignedOrderIds = spotAssignments.map(
      (assignment) => assignment.orderId,
    );

    return assignedOrderIds.filter(
      (orderId) => !savedOrderIds.includes(orderId),
    );
  }, [existingSpot, spotAssignments]);

  useEffect(() => {
    if (spotContractId !== null) {
      doSpotModalLazyQuery({
        variables: { voyageCharterId: spotContractId, outgoing: true },
      });
    }
  }, [doSpotModalLazyQuery, spotContractId]);

  const saveToastId = useRef<ReactText | null>(null);

  const [createSpotWithRelations] = useCreateSpotWithRelationsMutation();
  const [updateSpotWithRelations] = useUpdateSpotWithRelationsMutation();

  const handleSave = async (spot: SpotFormSaveType) => {
    saveToastId.current = toast('Saving Spot contract', {
      type: toast.TYPE.INFO,
    });

    try {
      if (!existingSpot) {
        const [createSpotRes] = await createSpotWithRelations(
          omit(spot.baseContract, 'id'),
          spot.relatedOrders.filter(isPersistedSpotOrder),
          spot.portData?.map((pd) => omit(pd, 'id')) ?? [],
          spot.commissions,
          spot.extraFreights,
        );

        const createdSpotId =
          createSpotRes.data?.createVoyageCharter?.voyageCharter?.id;

        assertDefined(createdSpotId, `expected new created spot to have id`);

        await doSpotModalLazyQuery({
          variables: { voyageCharterId: createdSpotId, outgoing: true },
        });

        onVesselChange?.(
          createdSpotId,
          spot.baseContract.shipownerOrgId,
          spot.baseContract.vesselId,
        );
      } else {
        await updateSpotWithRelations(
          {
            ...spot.baseContract,
            id: existingSpot.id,
          },
          existingSpot.orderRelations.nodes.filter(isDefined).map((rel) => {
            const freight = rel.freight.nodes[0];

            return {
              relationId: rel.id,
              orderId: rel.orderId,
              freightId: freight?.id ?? null,
              freightAmount: stringToNumOpt(freight?.freightAmount) ?? null,
              freightUnit: freight?.freightUnit ?? null,
            };
          }),
          spot.relatedOrders.filter(isPersistedSpotOrder),
          spot.portData ?? [],
          existingSpot.portDataRelations.nodes
            .map((pd) => pd?.portData)
            .filter(isDefined),
          spot.commissions,
          existingSpot.commissionsByVoyageCharterId.nodes.filter(isDefined),
          spot.extraFreights,
          existingSpot.voyageCharterExtraFreightsByVoyageCharterId.nodes.filter(
            isDefined,
          ),
          true,
        );

        await doSpotModalLazyQuery({
          variables: { voyageCharterId: existingSpot.id, outgoing: true },
        });

        if (
          existingSpot.shipownerOrgId !== spot.baseContract.shipownerOrgId ||
          existingSpot.vesselId !== spot.baseContract.vesselId
        ) {
          onVesselChange?.(
            existingSpot.id,
            spot.baseContract.shipownerOrgId,
            spot.baseContract.vesselId,
          );
        }
      }

      toast.update(saveToastId.current, {
        render: 'Spot contract saved successfully',
        type: toast.TYPE.SUCCESS,
      });
    } catch (error) {
      const errorMsg = error?.graphQLErrors[0]?.message ?? error.message;
      console.error('Error saving spot contract', errorMsg);
      const isDuplicateNameError = errorMsg.includes(
        'duplicate key value violates unique constraint "name_root_org_unique"',
      );
      const toastErrorMsg = isDuplicateNameError
        ? 'A contract with this name already exists, please use a unique name.'
        : '';
      toast.update(saveToastId.current, {
        render: `Error saving Spot contract. ${toastErrorMsg}`,
        type: toast.TYPE.ERROR,
      });
    }
  };

  if (spotDataLoading) {
    return <LoadingOverlay message="Loading spot contract" />;
  }
  return (
    <View flex={1}>
      <SpotForm
        onSave={handleSave}
        spot={existingSpot ?? null}
        unsavedOrderIds={unsavedOrderIds}
        outgoing
      />
    </View>
  );
};

export default SpotContract;
